import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  PlayIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  SparklesIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import {
  textToSpeechApi,
  translationApi,
  audioUtilsApi,
  TextToSpeechJob,
  Voice,
  Language,
} from '../../services/audioApi';
import { newsroomApi } from '@/services/newsroomApi';
import { AudioPageLayout, HistoryPanel, TransactionDrawer, HistoryItem } from '@/components/Audio';
import LocalizedTextArea from '@/components/Common/LocalizedTextArea';

type TTSProvider = 'elevenlabs' | 'sarvam' | 'ai4bharat';

const SAMPLE_TEXTS: Record<string, string> = {
  en: 'Hello! This is a sample text to demonstrate the text-to-speech functionality. You can replace this with your own content.',
  hi: 'नमस्ते! यह टेक्स्ट-टू-स्पीच कार्यक्षमता को प्रदर्शित करने के लिए एक नमूना पाठ है।',
  es: '¡Hola! Este es un texto de muestra para demostrar la funcionalidad de texto a voz.',
  fr: "Bonjour! Ceci est un texte d'exemple pour démontrer la fonctionnalité de synthèse vocale.",
  de: 'Hallo! Dies ist ein Beispieltext zur Demonstration der Text-zu-Sprache-Funktionalität.',
};

// Unicode-block based script detection — covers every language audio/languages
// returns (see sarvamService's supportedLanguages). Scripts shared by multiple
// languages (Devanagari: hi/mr/ne, Bengali: bn/as) collapse to one
// representative code; good enough to stop the dropdown defaulting to
// English for non-Latin text, which is all this is for.
const SCRIPT_LANGUAGE_RANGES: { code: string; start: number; end: number }[] = [
  { code: 'ur', start: 0x0600, end: 0x06ff }, // Arabic script
  { code: 'hi', start: 0x0900, end: 0x097f }, // Devanagari
  { code: 'bn', start: 0x0980, end: 0x09ff }, // Bengali
  { code: 'pa', start: 0x0a00, end: 0x0a7f }, // Gurmukhi
  { code: 'gu', start: 0x0a80, end: 0x0aff }, // Gujarati
  { code: 'or', start: 0x0b00, end: 0x0b7f }, // Odia
  { code: 'ta', start: 0x0b80, end: 0x0bff }, // Tamil
  { code: 'te', start: 0x0c00, end: 0x0c7f }, // Telugu
  { code: 'kn', start: 0x0c80, end: 0x0cff }, // Kannada
  { code: 'ml', start: 0x0d00, end: 0x0d7f }, // Malayalam
  { code: 'si', start: 0x0d80, end: 0x0dff }, // Sinhala
];

function detectLanguageFromScript(text: string): string | null {
  if (!text) return null;
  const counts: Record<string, number> = {};
  for (const ch of text) {
    const codePoint = ch.codePointAt(0);
    if (codePoint === undefined) continue;
    const range = SCRIPT_LANGUAGE_RANGES.find((r) => codePoint >= r.start && codePoint <= r.end);
    if (range) counts[range.code] = (counts[range.code] || 0) + 1;
  }
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

const TextToSpeech: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [text, setText] = useState<string>(() => searchParams.get('sourceText') || '');
  const [title, setTitle] = useState<string>(() => searchParams.get('sourceTitle') || '');
  // Arrived here via the news_article "Publish" next action — generating
  // speech must go through newsroomApi.publishArticle instead of the plain
  // TTS create call, since that's what also marks the article published,
  // records the article->TTS lineage link, and stores publishedTtsJobId.
  const sourceArticleId = searchParams.get('sourceArticleId');
  const [selectedVoice, setSelectedVoice] = useState<string>('');
  const [language, setLanguage] = useState<string>(() => detectLanguageFromScript(searchParams.get('sourceText') || '') || 'en');
  // Tracks whether the user has deliberately picked a language, so live
  // detection (below) doesn't fight a manual choice.
  const languageManuallySetRef = useRef(false);
  const [provider, setProvider] = useState<TTSProvider>('elevenlabs');
  const [speed, setSpeed] = useState<number>(1.0);
  const [stability, setStability] = useState<number>(0.5);
  const [similarityBoost, setSimilarityBoost] = useState<number>(0.5);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);

  // History and drawer state
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<HistoryItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [jobDetails, setJobDetails] = useState<TextToSpeechJob | null>(null);
  const [jobDetailsLoading, setJobDetailsLoading] = useState(false);
  const [retryText, setRetryText] = useState<string>('');

  // Pre-fill the editable retry text once per failed job — keyed off jobId
  // so it doesn't clobber in-progress edits on every polling refresh.
  useEffect(() => {
    if (jobDetails?.status === 'failed') {
      setRetryText(jobDetails.request?.text || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobDetails?.jobId, jobDetails?.status]);

  const { data: languagesData } = useQuery(
    ['audio-languages'],
    async () => (await audioUtilsApi.getSupportedLanguages()).data,
    { staleTime: 60 * 60 * 1000 }
  );
  const languages: Language[] = languagesData?.languages || [];

  const { data: voicesData, isLoading: voicesLoading } = useQuery(
    ['tts-voices'],
    async () => (await audioUtilsApi.getAvailableVoices()).data
  );
  const voices: Voice[] = voicesData?.voices || [];
  const filteredVoices = voices.filter((v) => v.language === language && v.provider === provider);

  useEffect(() => {
    // A voice belongs to exactly one provider — switching language or provider
    // can make the current selection invalid, so keep it in sync.
    if (!language || voices.length === 0) return;
    const stillValid = selectedVoice && filteredVoices.some((v) => v._id === selectedVoice);
    if (!stillValid) {
      setSelectedVoice(filteredVoices.length > 0 ? filteredVoices[0]._id : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, provider, voices]);

  const { data: jobsData } = useQuery(
    ['tts-jobs'],
    () => textToSpeechApi.getJobs({ limit: 50 }),
    {
      refetchInterval: (data: any) => {
        const hasActive = data?.data?.jobs?.some((j: any) => j.status === 'processing' || j.status === 'pending');
        return hasActive ? 3000 : 30000;
      },
    }
  );

  const { data: activeJobData } = useQuery(
    ['tts-job', currentJobId],
    () => textToSpeechApi.getTtsJob(currentJobId as string),
    {
      enabled: !!currentJobId,
      refetchInterval: (data: any) => {
        const status = data?.data?.status;
        return status === 'processing' || status === 'pending' ? 2000 : false;
      },
    }
  );
  const activeJob: TextToSpeechJob | undefined = activeJobData?.data;

  useEffect(() => {
    if (!activeJob) return;
    if (activeJob.status === 'completed' || activeJob.status === 'failed') {
      queryClient.invalidateQueries({ queryKey: ['tts-jobs'] });
    }
  }, [activeJob?.status, queryClient]);

  const generateMutation = useMutation({
    mutationFn: (payload: any) => textToSpeechApi.generateSpeech(payload),
    onSuccess: (response: any) => {
      const jobData = response?.data;
      if (!jobData?.jobId) {
        throw new Error(response?.message || 'Failed to start TTS job');
      }
      setCurrentJobId(jobData.jobId);
      toast.success('Speech generation started');
      queryClient.invalidateQueries({ queryKey: ['tts-jobs'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || error.message || 'Failed to generate speech');
    },
  });

  // Same destination job type, but creating it this way also marks the
  // source article published and records the lineage link — see
  // newsroomService.ts's publishArticle. The resulting ttsJobId is a real
  // TextToSpeechJob, so the existing activeJob polling above works on it
  // exactly the same as a job created the normal way.
  const publishArticleMutation = useMutation({
    mutationFn: (payload: { voiceId: string; provider?: TTSProvider }) =>
      newsroomApi.publishArticle(sourceArticleId as string, payload),
    onSuccess: (data) => {
      setCurrentJobId(data.ttsJobId);
      toast.success('Article published — speech generation started');
      queryClient.invalidateQueries({ queryKey: ['tts-jobs'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || error.message || 'Failed to publish article');
    },
  });

  const isProcessing = activeJob?.status === 'processing' || activeJob?.status === 'pending';
  const isSubmitting = generateMutation.isLoading || publishArticleMutation.isLoading;

  const generateSpeech = () => {
    if (!text.trim() || !selectedVoice) {
      toast.error('Please enter text and select a voice');
      return;
    }

    setCurrentJobId(null);

    if (sourceArticleId) {
      publishArticleMutation.mutate({ voiceId: selectedVoice, provider });
      return;
    }

    generateMutation.mutate({
      text: text.trim(),
      voiceId: selectedVoice,
      provider,
      language,
      ...(title ? { title } : {}),
      settings: { speed, stability, similarityBoost },
    });
  };

  const getLanguageName = (code?: string) => {
    if (!code) return undefined;
    const lang = languages.find((l) => l.code === code);
    return lang ? `${lang.name} (${lang.nativeName})` : code.toUpperCase();
  };

  const providerLabel = (p?: TTSProvider) =>
    p === 'sarvam' ? 'Sarvam' : p === 'ai4bharat' ? 'AI4Bharat' : 'ElevenLabs';

  // Map jobs to HistoryItem format whenever jobs data changes
  useEffect(() => {
    const jobs: TextToSpeechJob[] = jobsData?.data?.jobs || [];
    const items: HistoryItem[] = jobs.map((job) => ({
      id: job.jobId,
      title: job.title || (job.request?.text ? job.request.text.slice(0, 60) : `TTS Job ${job.jobId.slice(0, 8)}`),
      createdAt: job.createdAt,
      status: job.status,
      type: 'text-to-speech',
      metadata: {
        text: job.request?.text,
        voice: voices.find((v) => v._id === job.request?.voiceId)?.name || job.request?.voiceId,
        provider: job.request?.provider,
        language: getLanguageName(job.request?.language),
        format: job.result?.format,
        duration: job.result?.duration ? `${job.result.duration.toFixed(1)}s` : undefined,
        audioUrl: job.status === 'completed' ? job.result?.audioUrl : undefined,
        progress: job.status === 'processing' || job.status === 'pending' ? `${job.progress}%` : undefined,
      },
    }));
    setHistoryItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsData, languages, voices]);

  const handleHistoryItemClick = (item: HistoryItem) => {
    setSelectedHistoryItem(item);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => { setSelectedHistoryItem(null); setJobDetails(null); }, 300);
  };

  const refreshJobDetails = async (jobId: string) => {
    try {
      const res = await textToSpeechApi.getTtsJob(jobId);
      const job = res?.data;
      if (job?.jobId) setJobDetails(job);
    } catch { /* silent */ }
  };

  const handleRetryJob = async (jobId: string, text?: string) => {
    try {
      await textToSpeechApi.retryJob(jobId, text !== undefined ? { text } : undefined);
      toast.success('Retrying job');
      queryClient.invalidateQueries({ queryKey: ['tts-jobs'] });
      refreshJobDetails(jobId);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to retry job');
    }
  };

  // Fetch full job details when drawer opens
  useEffect(() => {
    if (!isDrawerOpen || !selectedHistoryItem) { setJobDetails(null); return; }
    const jobs: TextToSpeechJob[] = jobsData?.data?.jobs || [];
    const cached = jobs.find((j) => j.jobId === selectedHistoryItem.id);
    if (cached) setJobDetails(cached);
    setJobDetailsLoading(true);
    textToSpeechApi.getTtsJob(selectedHistoryItem.id)
      .then((res: any) => {
        const job = res?.data;
        if (job?.jobId) setJobDetails(job);
      })
      .catch(console.error)
      .finally(() => setJobDetailsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, selectedHistoryItem?.id]);

  // Keep polling while the drawer is open and the job is still processing
  useEffect(() => {
    if (!isDrawerOpen || !selectedHistoryItem) return;
    if (jobDetails?.status !== 'processing' && jobDetails?.status !== 'pending') return;
    const timer = setTimeout(() => refreshJobDetails(selectedHistoryItem.id), 2000);
    return () => clearTimeout(timer);
  }, [isDrawerOpen, selectedHistoryItem?.id, jobDetails]);

  const downloadAudio = (job: TextToSpeechJob) => {
    if (!job.result?.audioUrl) return;
    const ext = job.result.format || 'mp3';
    translationApi.downloadFile(job.result.audioUrl, `${job.title || 'speech'}-${job.jobId.slice(0, 8)}.${ext}`);
  };

  const copyText = () => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const pasteText = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      setText(clipboardText);
    } catch {
      toast.error('Failed to read clipboard');
    }
  };

  const clearText = () => {
    setText('');
    setTitle('');
    setCurrentJobId(null);
  };

  const insertSampleText = () => {
    setText(SAMPLE_TEXTS[language] || SAMPLE_TEXTS.en);
  };

  const handleCancelForm = () => {
    setText('');
    setTitle('');
    setSelectedVoice('');
    languageManuallySetRef.current = false;
    setLanguage('en');
    setProvider('elevenlabs');
    setSpeed(1.0);
    setStability(0.5);
    setSimilarityBoost(0.5);
    setShowAdvanced(false);
    setCurrentJobId(null);
  };

  return (
    <>
      <AudioPageLayout
        title="Text to Speech"
        formSection={
          <div className="space-y-6">
            {sourceArticleId && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                Publishing this article — generating speech here will mark the article as published and link it to this job.
              </div>
            )}
            {/* Text Input Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Text Input</h2>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={insertSampleText}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <SparklesIcon className="h-3.5 w-3.5" /> Sample
                  </button>
                  <button
                    type="button"
                    onClick={pasteText}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" /> Paste
                  </button>
                  <button
                    type="button"
                    onClick={copyText}
                    disabled={!text.trim()}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    <DocumentDuplicateIcon className="h-3.5 w-3.5" /> Copy
                  </button>
                  <button
                    type="button"
                    onClick={clearText}
                    disabled={!text.trim()}
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
                  >
                    <TrashIcon className="h-3.5 w-3.5" /> Clear
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Speech Title <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Welcome Message - Spanish Version"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  maxLength={200}
                />
              </div>

              <LocalizedTextArea
                language={language}
                value={text}
                onChange={(e) => {
                  const value = e.target.value;
                  setText(value);
                  if (!languageManuallySetRef.current) {
                    const detected = detectLanguageFromScript(value);
                    if (detected && detected !== language) setLanguage(detected);
                  }
                }}
                className="w-full h-56 p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none dark:bg-gray-900 dark:text-white"
                placeholder="Enter the text you want to convert to speech..."
                maxLength={5000}
              />

              <div className="flex justify-between items-center mt-3 text-sm text-gray-500 dark:text-gray-400">
                <span>Characters: {text.length} / 5000</span>
                <span>Words: {text.split(/\s+/).filter((w) => w.length > 0).length}</span>
              </div>
            </div>

            {/* Speech Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Speech Settings</h2>

              <div className="space-y-4">
                {/* Language */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Language</label>
                  <div className="relative">
                    <select
                      value={language}
                      onChange={(e) => {
                        languageManuallySetRef.current = true;
                        setLanguage(e.target.value);
                      }}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white bg-white text-gray-900 appearance-none cursor-pointer"
                    >
                      {languages.map((lang) => (
                        <option key={lang.code} value={lang.code}>{lang.name} ({lang.nativeName})</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>

                {/* Provider */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Text-to-Speech Engine</label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setProvider('elevenlabs')}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        provider === 'elevenlabs'
                          ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-base">🗣️</span> ElevenLabs
                      {provider === 'elevenlabs' && <CheckCircleIcon className="h-4 w-4 ml-auto" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setProvider('sarvam')}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        provider === 'sarvam'
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-base">🇮🇳</span> Sarvam
                      {provider === 'sarvam' && <CheckCircleIcon className="h-4 w-4 ml-auto" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setProvider('ai4bharat')}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        provider === 'ai4bharat'
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-base">🔬</span> AI4Bharat
                      {provider === 'ai4bharat' && <CheckCircleIcon className="h-4 w-4 ml-auto" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {provider === 'sarvam'
                      ? 'Sarvam TTS — supports pace adjustment for Indian languages'
                      : provider === 'ai4bharat'
                      ? 'IndicF5 — voice-cloned TTS, requires reference audio'
                      : 'ElevenLabs — default multilingual TTS with speed control'}
                  </p>
                </div>

                {/* Voice */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Voice *</label>
                  <div className="relative">
                    <select
                      value={selectedVoice}
                      onChange={(e) => setSelectedVoice(e.target.value)}
                      disabled={voicesLoading}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white bg-white text-gray-900 appearance-none cursor-pointer disabled:opacity-50"
                    >
                      <option value="" disabled>{voicesLoading ? 'Loading voices...' : 'Select a voice'}</option>
                      {filteredVoices.map((voice) => (
                        <option key={voice._id} value={voice._id}>{voice.name}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  {filteredVoices.length === 0 && !voicesLoading && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      No {providerLabel(provider)} voices available for {getLanguageName(language)}
                    </p>
                  )}
                </div>

                {/* Speed */}
                {provider !== 'ai4bharat' && (
                  <div>
                    <label className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <span>Speech speed</span>
                      <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{speed.toFixed(1)}x</span>
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="2.0"
                      step="0.1"
                      value={speed}
                      onChange={(e) => setSpeed(parseFloat(e.target.value))}
                      className="w-full h-2 accent-indigo-600 dark:accent-indigo-400 cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span>Slow (0.5x)</span>
                      <span>Normal (1.0x)</span>
                      <span>Fast (2.0x)</span>
                    </div>
                  </div>
                )}

                {/* Advanced (ElevenLabs only) */}
                {provider === 'elevenlabs' && (
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {showAdvanced ? 'Hide' : 'Show'} advanced settings
                    </button>

                    {showAdvanced && (
                      <div className="space-y-4 mt-3 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg">
                        <div>
                          <label className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <span>Stability</span>
                            <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{stability.toFixed(2)}</span>
                          </label>
                          <input
                            type="range" min="0" max="1" step="0.01" value={stability}
                            onChange={(e) => setStability(parseFloat(e.target.value))}
                            className="w-full h-2 accent-indigo-600 dark:accent-indigo-400 cursor-pointer"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Higher values make speech more consistent</p>
                        </div>
                        <div>
                          <label className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <span>Similarity Boost</span>
                            <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{similarityBoost.toFixed(2)}</span>
                          </label>
                          <input
                            type="range" min="0" max="1" step="0.01" value={similarityBoost}
                            onChange={(e) => setSimilarityBoost(parseFloat(e.target.value))}
                            className="w-full h-2 accent-indigo-600 dark:accent-indigo-400 cursor-pointer"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Higher values make speech more similar to the original voice</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Processing Progress */}
              {isProcessing && (
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center text-sm font-medium text-blue-800 dark:text-blue-300">
                      <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" />
                      {activeJob?.currentStep || 'Generating speech...'}
                    </div>
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-300">{activeJob?.progress ?? 0}%</span>
                  </div>
                  <div className="w-full bg-blue-200 dark:bg-blue-900/40 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${activeJob?.progress ?? 0}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Failed */}
              {activeJob?.status === 'failed' && (
                <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">Speech generation failed</p>
                  <p className="text-sm text-red-600 dark:text-red-400">{activeJob.error}</p>
                </div>
              )}

              {/* Submit */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancelForm}
                  disabled={isSubmitting || isProcessing}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={generateSpeech}
                  disabled={!text.trim() || !selectedVoice || isSubmitting || isProcessing}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" /> Generating Speech...
                    </>
                  ) : isSubmitting ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" /> Starting...
                    </>
                  ) : (
                    <>
                      <PlayIcon className="h-4 w-4" /> {sourceArticleId ? 'Publish Article' : 'Generate Speech'}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Generated Audio Result */}
            {activeJob?.status === 'completed' && activeJob.result && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Generated Audio</h3>

                {activeJob.request?.text && (
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Original Text</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3 whitespace-pre-wrap">
                      {activeJob.request.text}
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <SpeakerWaveIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Generated Speech</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(activeJob.result.format || 'mp3').toUpperCase()} • {activeJob.result.duration?.toFixed(1)}s
                    </p>
                  </div>
                </div>

                <audio controls src={activeJob.result.audioUrl} className="w-full h-10" />

                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => downloadAudio(activeJob)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Download Audio ({(activeJob.result.format || 'mp3').toUpperCase()})
                  </button>
                </div>
              </div>
            )}
          </div>
        }
        historySection={
          <HistoryPanel
            items={historyItems}
            onItemClick={handleHistoryItemClick}
            selectedItemId={selectedHistoryItem?.id}
            renderItemActions={(item) => {
              if (item.status !== 'completed' || !item.metadata?.audioUrl) return null;
              return (
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => translationApi.downloadFile(item.metadata!.audioUrl, `tts-${item.id}.${item.metadata?.format || 'mp3'}`)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                  >
                    <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                    Download Audio
                  </button>
                </div>
              );
            }}
          />
        }
      />

      <TransactionDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        item={selectedHistoryItem}
        size="vw60"
        hideMetadata
        title={selectedHistoryItem?.title}
        renderDetails={() => {
          const job = jobDetails;

          const stepStatusIcon = (status?: string) => {
            switch (status) {
              case 'completed': return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
              case 'processing': return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
              case 'failed': return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
              default: return <ClockIcon className="h-5 w-5 text-yellow-500" />;
            }
          };

          const detailRow = (label: string, value?: string | null) => {
            if (!value) return null;
            return (
              <div key={label} className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white text-right max-w-[60%]">{value}</span>
              </div>
            );
          };

          return (
            <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
              {/* Status */}
              <div className="flex items-center gap-2">
                {stepStatusIcon(job?.status)}
                <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{job?.status || 'Loading…'}</span>
                {(job?.status === 'processing' || job?.status === 'pending') && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-auto">{job?.progress ?? 0}%</span>
                )}
              </div>

              {(job?.status === 'processing' || job?.status === 'pending') && (
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                  <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${job?.progress ?? 0}%` }} />
                </div>
              )}

              {/* Request settings */}
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Settings</h3>
                <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-1">
                  {detailRow('Language', getLanguageName(job?.request?.language))}
                  {detailRow('Engine', providerLabel(job?.request?.provider as TTSProvider))}
                  {detailRow('Output Format', job?.request?.outputFormat)}
                </div>
              </div>

              {/* Original text — editable when the job failed, so a bad/garbled request can be fixed before retrying */}
              {job?.request?.text && (
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                    {job.status === 'failed' ? 'Text (edit before retrying)' : 'Original Text'}
                  </h3>
                  {job.status === 'failed' ? (
                    <>
                      <textarea
                        value={retryText}
                        onChange={(e) => setRetryText(e.target.value)}
                        rows={6}
                        maxLength={5000}
                        className="w-full text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3 whitespace-pre-wrap resize-y border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-right">{retryText.length} / 5000</p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3 whitespace-pre-wrap">
                      {job.request.text}
                    </p>
                  )}
                </div>
              )}

              {/* Result */}
              {job?.status === 'completed' && job?.result && (
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">Generated Audio</h3>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 space-y-2">
                    <audio controls src={job.result.audioUrl} className="w-full h-9" />
                    <p className="text-xs text-blue-700 dark:text-blue-400 text-center">
                      {(job.result.format || 'mp3').toUpperCase()} • {job.result.duration?.toFixed(1)}s
                      {job.result.size ? ` • ${(job.result.size / 1024).toFixed(0)} KB` : ''}
                    </p>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/app/create-video?ttsJobId=${job.jobId}`)}
                        className="flex items-center justify-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-xs font-medium rounded-md transition-colors"
                      >
                        <SparklesIcon className="h-3.5 w-3.5" />
                        Create Video from this audio
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadAudio(job)}
                        className="flex items-center justify-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors"
                      >
                        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                        Download Audio
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Error */}
              {job?.error && (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">Error</p>
                  <p className="text-sm text-red-600 dark:text-red-300">{job.error}</p>
                </div>
              )}

              {job?.status === 'failed' && (
                <button
                  type="button"
                  onClick={() => handleRetryJob(job.jobId, retryText)}
                  disabled={!retryText.trim()}
                  className="w-full flex items-center justify-center gap-2 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-md border border-indigo-200 dark:border-indigo-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowPathIcon className="h-3.5 w-3.5" />
                  Retry Job
                </button>
              )}

              {jobDetailsLoading && !job && (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <ArrowPathIcon className="h-3 w-3 animate-spin" /> Loading details…
                </div>
              )}
            </div>
          );
        }}
      />
    </>
  );
};

export default TextToSpeech;
